<script>
    import { getContext, onMount } from 'svelte';
    import { Policy, Language } from "@Store";
    import { push } from 'svelte-spa-router';

    import BillingInfo from "@components/BillingInfo.svelte";
    import StripePayment from "@components/StripePayment.svelte";
    
    let billingComplete = false;
    let billingData = null;
    let stripeCheckout;
    let stripeReady = false;
    let loading = false;

    let buttonMessage = "Complete Billing Info";
    let showSubmittedModal = false;

    onMount(() => {
        if ($Policy && !$Policy.valid) {
            console.log('Policy is not valid, redirecting to applicant');
            push(`/${$Policy.uuid}/applicant`);
        }
        if ($Policy && $Policy.valid && !$Policy.declaration) {
            console.log('Policy is valid but not declaration, redirecting to summary');
            push(`/${$Policy.uuid}/summary`);
        }
    });

    function handleBillingComplete(event) {
        billingComplete = event.detail.valid;
        billingData = event.detail.data;
        
        if (billingComplete) {
            buttonMessage = "Complete Payment";
        } else {
            buttonMessage = "Complete Billing Info";
        }
    }

    function onStripeReady() {
        stripeReady = true;
    }

    async function processPayment() {
        if (!billingComplete) {
            alert('Please complete all billing information fields');
            return;
        }

        if (!stripeReady) {
            alert('Please complete card details');
            return;
        }

        loading = true;
        buttonMessage = "Processing Payment...";
        await stripeCheckout.processPayment();
    }

    async function onPaymentComplete() {
        // reload the policy
        const updated = await Insurecert.API.policy($Policy.uuid).get();
        if (updated) {
            $Policy = updated;
        }
        buttonMessage = "Payment Complete!";
        loading = false;
        push(`/${$Policy.uuid}/complete`);
    }

    function onPaymentError(event) {
        console.error('Payment error:', event.detail);
        buttonMessage = "Complete Payment";
        loading = false;
    }

    // Format currency
    function formatCurrency(amount) {
        if (amount == null || isNaN(amount)) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(parseFloat(amount));
    }

    $: premium = $Policy.premium || 0;
    $: taxTotal = $Policy?.taxtotal ?? 0;
    $: serviceFee = $Policy.service_fee || 0;
    $: total = $Policy.total || 0;
    $: isPending = ($Policy.status === 'pending');

    async function submitApplication() {
        if (!billingComplete) {
            alert('Please complete all billing information fields');
            return;
        }
        loading = true;
        try {
            // Save latest billing info if available
            // (Optional, since BillingInfo already debounces & saves)
            console.log('Submitting application', $Policy.uuid);
            await Insurecert.API.policy($Policy.uuid).submitRequest();
            console.log('Application submitted', $Policy.uuid);
            // Refresh policy and route based on status
            const updated = await Insurecert.API.policy($Policy.uuid).get();
            if (updated) {
                $Policy = updated;
                // If backend flips it to completed, go to complete; else stay and show success
                if ($Policy.status === 'completed') {
                    push(`/${$Policy.uuid}/complete`);
                    return;
                }
            }
            buttonMessage = "Application Submitted";
            showSubmittedModal = true;
        } catch (e) {
            console.error('Submit application error:', e);
            alert('There was an error submitting your application. Please try again.');
        } finally {
            loading = false;
        }
    }

    function goToSignIn() {
        push('/dashboard');
    }

    function startNewApplication() {
        const url = new URL(window.location.href);
        url.searchParams.set('new', 'true');
        window.location.href = url.toString(); // triggers your new-policy flow
    }
</script>

<div class="checkout-page">
    <div class="checkout-header">
        <h1>{$Language === 'en' ? 'Checkout' : 'Checkout'}</h1>
        <p class="sub">{$Language === 'en' ? 'Complete your purchase to activate your policy' : 'Complétez votre achat pour activer votre politique'}</p>
    </div>

       <!-- Pricing Summary -->
    {#if !isPending}
        <div class="pricing-summary">
            <div class="pricing-card">
                <div class="pricing-header">
                    <h2>{$Language === 'en' ? 'Order Summary' : 'Résumé de la commande'}</h2>
                </div>
                <div class="pricing-body">
                    <div class="pricing-row">
                        <span class="pricing-label">{$Language === 'en' ? 'Premium' : 'Prime'}</span>
                        <span class="pricing-value">${premium}</span>
                    </div>
                    <div class="pricing-row">
                        <span class="pricing-label">{$Language === 'en' ? 'Taxes' : 'Impôts'}</span>
                        <span class="pricing-value">{formatCurrency(taxTotal)}</span>
                    </div>
                    <div class="pricing-row">
                        <span class="pricing-label">{$Language === 'en' ? 'Service Fee' : 'Frais de service'}</span>
                        <span class="pricing-value">${serviceFee}</span>
                    </div>
                    <div class="pricing-row total">
                        <span class="pricing-label">{$Language === 'en' ? 'Total Due' : 'Total dû'}</span>
                        <span class="pricing-value">${total}</span>
                    </div>
                </div>
            </div>
        </div>
        {:else}
        <div class="pending-alert">
            <div class="pending-card">
                <div class="pending-header">
                    <h2>{$Language === 'en' ? 'We couldn’t calculate a price online' : 'Nous n\'avons pas pu calculer un prix en ligne'}</h2>
                </div>
                <div class="pending-body">
                    <p>
                        {$Language === 'en' ? 'Your application requires additional review. Submit your application and our team will complete the review and follow up by email.' : 'Votre application nécessite une révision supplémentaire. Soumettez votre application et notre équipe complétera la révision et vous suivra par email.'}
                    </p>
                </div>
            </div>
        </div>
    {/if}

    <!-- Billing Information -->
    <div class="section">
        <div class="section-header">
            <h2>Billing Information</h2>
            {#if billingComplete}
                <span class="status-badge complete">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    {$Language === 'en' ? 'Complete' : 'Complet'}
                </span>
            {:else}
                <span class="status-badge incomplete">{$Language === 'en' ? 'Required' : 'Requis'}</span>
            {/if}
        </div>
        <BillingInfo on:billingComplete={handleBillingComplete} />
    </div>
    
    <!-- Payment Information -->
    <!-- {#if isPending}
        <div class="pending-alert">
            <div class="pending-card">
                <div class="pending-header">
                    <h2>Manual review required</h2>
                </div>
                <div class="pending-body">
                    <p>
                        We couldn’t calculate a price online. Submit your application and our team will complete the review and follow up by email.
                    </p>
                </div>
            </div>
        </div>
    {/if} -->
    {#if billingComplete && !isPending}
        <div class="section">
            <div class="section-header">
                <h2>Payment Information</h2>
                {#if stripeReady}
                    <span class="status-badge complete">
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Ready
                    </span>
                {:else}
                    <span class="status-badge incomplete">{$Language === 'en' ? 'Required' : 'Requis'}</span>
                {/if}
            </div>
            <StripePayment 
                {billingData} 
                bind:this={stripeCheckout}
                on:ready={onStripeReady}
                on:complete={onPaymentComplete}
                on:error={onPaymentError}
            />
        </div>
    {/if}

    <!-- Action Button -->
    <div class="actions">
        {#if isPending}
            <button 
                class="pay-button"
                on:click={submitApplication}
                disabled={!billingComplete || loading}
            >
                {#if loading}<span class="spinner"></span>{/if}
                {loading ? ($Language === 'en' ? 'Submitting...' : 'Soumission en cours...') : ($Language === 'en' ? 'Submit Application' : 'Soumettre l\'application')}
            </button>
            {#if !billingComplete}
                <div class="hint">{$Language === 'en' ? 'Complete billing information to continue' : 'Complétez les informations de facturation pour continuer'}</div>
            {/if}
        {:else}
            <button 
                class="pay-button"
                on:click={processPayment} 
                disabled={!billingComplete || !stripeReady || loading}
            >
                {#if loading}
                    <span class="spinner"></span>
                {/if}
                {buttonMessage}
            </button>
            {#if !billingComplete}
                <div class="hint">{$Language === 'en' ? 'Complete billing information to continue' : 'Complétez les informations de facturation pour continuer'}</div>
            {:else if !stripeReady}
                <div class="hint">{$Language === 'en' ? 'Enter your card details to complete payment' : 'Entrez les détails de votre carte pour compléter le paiement'}</div>
            {/if}
        {/if}
    </div>
</div>

{#if showSubmittedModal}
<div class="modal-backdrop" role="dialog" aria-modal="true">
    <div class="modal-card">
        <div class="modal-header">
            <div class="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M7 12l3 3 7-7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
            <h3>{$Language === 'en' ? 'Application submitted' : 'Application soumise'}</h3>
            <p>{$Language === 'en' ? 'Your application was forwarded for manual review. We’ll email you with next steps.' : 'Votre application a été envoyée pour examen manuel. Nous vous enverrons un email avec les prochaines étapes.'}</p>
        </div>
        <div class="modal-actions">
            <button class="modal-primary" on:click={goToSignIn}>{$Language === 'en' ? 'Sign in' : 'Se connecter'}</button>
            <button class="modal-secondary" on:click={startNewApplication}>{$Language === 'en' ? 'Start new application' : 'Commencer une nouvelle application'}</button>
        </div>
    </div>
</div>
{/if}

<style>
    .checkout-page {
        max-width: 820px;
        margin: 0 auto;
        padding: 2rem 1rem 3rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .checkout-header {
        text-align: center;
        margin-bottom: 2rem;
    }

    .checkout-header h1 {
        margin: 0 0 0.5rem 0;
        font-size: 1.75rem;
        letter-spacing: -0.02em;
        color: #1d1d1f;
        font-weight: 700;
    }

    .checkout-header .sub {
        color: #666;
        margin: 0;
        font-size: 1rem;
    }

    /* Pricing Summary */
    .pricing-summary {
        margin-bottom: 2rem;
    }

    .pricing-card {
        background: linear-gradient(135deg, #fffbf0 0%, #ffffff 100%);
        border: 2px solid #f5d99f;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(59, 130, 246, 0.08);
    }

    .pricing-header {
        padding: 1.25rem 1.5rem;
        border-bottom: 2px solid #f5d99f;
        background: linear-gradient(135deg, #fef3dc 0%, #fffcf5 100%);
    }

    .pricing-header h2 {
        margin: 0;
        font-size: 1.125rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .pricing-body {
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 0.875rem;
        /* background: #fff; */
    }

    .pricing-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.625rem 0;
        border-bottom: 1px solid #f5e5c5;
    }

    .pricing-row:last-child {
        border-bottom: none;
    }

    .pricing-row.total {
        margin-top: 0.5rem;
        padding-top: 1rem;
        border-top: 2px solid var(--gold);
        border-bottom: none;
    }

    .pricing-label {
        font-size: 0.9375rem;
        color: #6b7280;
        font-weight: 600;
        letter-spacing: -0.01em;
    }

    .pricing-row.total .pricing-label {
        font-size: 1.125rem;
        color: #1d1d1f;
        font-weight: 700;
    }

    .pricing-value {
        font-size: 0.9375rem;
        color: #1d1d1f;
        font-weight: 600;
        letter-spacing: -0.01em;
    }

    .pricing-row.total .pricing-value {
        font-size: 1.375rem;
        color: var(--gold);
        font-weight: 700;
    }

    /* Section */
    .section {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
        margin-bottom: 1.5rem;
    }

    .section-header {
        padding: 1.25rem 1.5rem;
        border-bottom: 2px solid #e9ecef;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .section-header h2 {
        margin: 0;
        font-size: 1.125rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .status-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        padding: 0.375rem 0.75rem;
        border-radius: 999px;
        font-size: 0.8125rem;
        font-weight: 600;
        letter-spacing: -0.01em;
    }

    .status-badge.complete {
        background: rgba(15, 157, 88, 0.12);
        color: #0f9d58;
        border: 1px solid rgba(15, 157, 88, 0.25);
    }

    .status-badge.complete svg {
        width: 14px;
        height: 14px;
    }

    .status-badge.incomplete {
        background: rgba(251, 146, 60, 0.12);
        color: #f59e0b;
        border: 1px solid rgba(251, 146, 60, 0.25);
    }

    /* Actions */
    .actions {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        margin-top: 2rem;
    }

    .pay-button {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 14px;
        padding: 0.875rem 2rem;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 700;
        color: #fff;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        letter-spacing: -0.01em;
        box-shadow: 0 6px 20px rgba(49, 104, 50, 0.25);
        display: flex;
        align-items: center;
        gap: 0.625rem;
        min-width: 220px;
        justify-content: center;
    }

    .pay-button:hover:not([disabled]) {
        transform: translateY(-2px);
        box-shadow: 0 10px 28px rgba(49, 104, 50, 0.35);
    }

    .pay-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        box-shadow: none;
    }

    .spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    .hint {
        color: #6b7280;
        font-size: 0.95rem;
        text-align: center;
    }

    @media (min-width: 640px) {
        .pricing-body {
            padding: 2rem;
        }
    }

    .pending-alert {
        margin-bottom: 1.25rem;
    }
    .pending-card {
        background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%);
        border: 2px solid #f5c2c2;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(220, 53, 69, 0.1);
    }
    .pending-header {
        padding: 1.25rem 1.5rem;
        border-bottom: 2px solid #f5c2c2;
        background: linear-gradient(135deg, #ffe3e3 0%, #fff8f8 100%);
    }
    .pending-header h2 {
        margin: 0;
        font-size: 1.125rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }
    .pending-body {
        padding: 1.25rem 1.5rem;
        color: #1d1d1f;
    }

    .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(17, 24, 39, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 50;
        padding: 1rem;
    }

    .modal-card {
        max-width: 540px;
        width: 100%;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        box-shadow: 0 12px 40px rgba(0,0,0,0.15);
        padding: 1.5rem;
    }

    .modal-header {
        text-align: center;
    }

    .icon-wrap {
        width: 52px;
        height: 52px;
        border-radius: 999px;
        margin: 0 auto 0.75rem;
        background: rgba(49, 104, 50, 0.12);
        color: var(--base-green);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .icon-wrap svg {
        width: 26px;
        height: 26px;
    }

    .modal-header h3 {
        margin: 0.25rem 0 0.25rem 0;
        font-size: 1.25rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .modal-header p {
        margin: 0;
        color: #4b5563;
        font-size: 0.975rem;
    }

    .modal-actions {
        margin-top: 1.25rem;
        display: flex;
        gap: 0.75rem;
        justify-content: center;
        flex-wrap: wrap;
    }

    .modal-primary {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 12px;
        padding: 0.75rem 1.25rem;
        color: #fff;
        font-weight: 700;
        cursor: pointer;
        box-shadow: 0 6px 20px rgba(49, 104, 50, 0.25);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .modal-primary:hover {
        transform: translateY(-1px);
        box-shadow: 0 10px 28px rgba(49, 104, 50, 0.35);
    }

    .modal-secondary {
        background: #fff;
        border: 2px solid #e5e7eb;
        border-radius: 12px;
        padding: 0.75rem 1.25rem;
        color: #1d1d1f;
        font-weight: 700;
        cursor: pointer;
        transition: background 0.2s ease, border-color 0.2s ease;
    }

    .modal-secondary:hover {
        background: #f9fafb;
        border-color: #d1d5db;
    }
</style>